/**
 * @fileoverview Modal component for confirming the deletion of a custom company list view.
 * @description This component displays a confirmation dialog before deleting a saved view.
 * It receives modal controls and the item to delete from its parent.
 * @active Client component [CONFIRMED]. Used within the company list view tools.
 *
 * @see {@link src/components/companies/companiesList/newCompaniesList/viewListTools/view/index.tsx} - Parent component that uses this modal.
 * @see {@link src/components/ui/modals/DefaultModal.tsx} - Base modal component used.
 * @see {@link src/components/ui/auth/FormButton.tsx} - Button component used.
 */

import { Box, Flex, Text } from "@chakra-ui/react";

import FormButton from "@/components/ui/auth/FormButton";
import DefaultModal from "@/components/ui/modals/DefaultModal";

/**
 * Modal component for confirming deletion of a view
 * @param {Object} props - Component props
 * @param {object} props.deleteViewModalControl - Chakra UI modal control object (`useDisclosure`).
 * @param {boolean} props.deleteViewModalControl.isOpen - Whether the modal is open.
 * @param {() => void} props.deleteViewModalControl.onClose - Function to close the modal.
 * @param {() => void} props.deleteHandler - Function called when the 'Yes' button is clicked.
 * @param {{ title: string } | null} props.selectedItemToDelete - The view object to be deleted (should not be null when modal is open).
 * @param {string} [props.selectedItemToDelete.title] - Title of the view to display in the confirmation message.
 * @see {@link src/components/ui/modals/DefaultModal.tsx}
 * @see {@link src/components/ui/auth/FormButton.tsx}
 */
const DeleteNewViewModal = ({
  deleteViewModalControl,
  deleteHandler,
  selectedItemToDelete, // Type allows null, but practically shouldn't be null when rendered
}: {
  deleteViewModalControl: { isOpen: boolean; onClose: () => void };
  deleteHandler: () => void;
  selectedItemToDelete: { title?: string } | null; // Allow title to be optional or item to be null
}) => {
  // The modal should ideally only be rendered when selectedItemToDelete is not null
  // by the parent component (CompaniesView).
  return (
    <DefaultModal
      {...{
        isOpen: deleteViewModalControl.isOpen,
        onClose: deleteViewModalControl.onClose,
        content_style: {
          p: "12px",
        },
        modalStyle: {
          borderRadius: "4px",
        },
        isCloseBtn: false,
      }}
    >
      <Flex direction={"column"} maxW={"222px"} rowGap={"12px"}>
        <Text textAlign={"center"} maxW={"185px"}>
          Do you want to remove{" "}
          <Box as="span">{selectedItemToDelete?.title}</Box>?
        </Text>
        <Flex align={"centre"} columnGap={"8px"} px={"9px"}>
          <FormButton
            {...{
              title: "No",
              button: {
                bg: "transparent",
                borderColor: "mainBlue",
                borderWidth: "1px",
                color: "mainBlue",
                h: "30px",
                p: "0",
                w: "81px",
                _hover: { bg: "lightBlue2" },
                onClick: deleteViewModalControl?.onClose,
              },
            }}
          />
          <FormButton
            {...{
              title: "Yes",
              button: {
                p: "0",
                h: "30px",
                w: "81px",
                onClick: deleteHandler,
              },
            }}
          />
        </Flex>
      </Flex>
    </DefaultModal>
  );
};

export default DeleteNewViewModal;
